import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../../../styles/payrole-system/payrole-processor.css";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import { convertThenFormat1, getMonthYear } from "../../common-data/Converts";
import { ReloadIcon } from "../../../images/icons/icons";
import { fetchExportApiData } from "../../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";

const BulkPayrollProcessing = ({
  callFunction,
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,
  setStatsData,
  filters,
  userInfoData,
}) => {
  const dispatch = useDispatch();
  const { employeePayrollData, employeePayrollLoading, employeePayrollError } =
    useSelector((state) => state.payrole);
  const { exportData, exportLoading } = useSelector((state) => state.common);

  const [isExportReady, setIsExportReady] = useState(false);

  const isMonthFilterSelected = filters?.month || false;

  const iniDatas = {
    pagination: [],
    allData: [],
  };
  const [allDatas, setAllDatas] = useState(iniDatas);

  const [selectedIds, setSelectedIds] = useState([]);

  const allSelected =
    selectedIds.length === allDatas.allData?.length &&
    allDatas.allData?.length > 0;
  const anySelected = selectedIds.length > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allDatas.allData?.map((e) => e?.user_id) || []);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const getTotalPayout = () => {
    return allDatas.allData
      .filter((e) => selectedIds.includes(e?.user_id))
      .reduce(
        (sum, e) => sum + parseFloat(e?.net_salary?.replace(/[^\d.]/g, "")),
        0
      );
  };

  const handleExportSelected = () => {
    const handleConfirm = async () => {
      const { month } = filters;

      const sendableFilters = {
        month,
        user_ids: selectedIds.join(","),
      };

      dispatch(
        fetchExportApiData({
          method: "GET",
          endpoint: "/payrole/export",
          payload: {
            filters: sendableFilters,
          },
          token: userInfoData?.token,
        })
      );
    };

    showConfirmAlert(
      handleConfirm,
      `Confirm Before Exporting!`,
      "Cancel",
      "Export"
    );
  };

  useEffect(() => {
    if (
      !employeePayrollLoading &&
      !employeePayrollError &&
      employeePayrollData?.status &&
      employeePayrollData?.data?.length > 0
    ) {
      setAllDatas((prevs) => ({
        ...prevs,
        allData: employeePayrollData.data,
        pagination:
          employeePayrollData?.pagination?.length > 0
            ? employeePayrollData.pagination
            : [],
      }));
    } else {
      setAllDatas(iniDatas);
    }
  }, [employeePayrollLoading, employeePayrollError, employeePayrollData]);

  useEffect(() => {
    if (allDatas.allData?.length > 0) {
      setStatsData((prevs) => ({
        ...prevs,
        total_eligible_employees: allDatas.pagination?.[0]?.pagination || 0,
        total_selected_employees: selectedIds.length,
        total_processed_employees: selectedIds.length,
        total_payout: getTotalPayout(),
      }));
    }
  }, [allDatas.pagination, selectedIds]);

  useEffect(() => {
    setSelectedIds([]);
    setIsExportReady(false);
  }, [filters.month]);

  useEffect(() => {
    setIsExportReady(false);
  }, [selectedIds]);

  useEffect(() => {
    if (exportData && !exportLoading) {
      setIsExportReady(true);
    }
  }, [exportData, exportLoading]);

  const columns = [
    {
      dataField: "select",
      text: (
        <input
          type="checkbox"
          className="pp-checkbox"
          checked={isMonthFilterSelected ? allSelected : false}
          onChange={isMonthFilterSelected ? handleSelectAll : null}
          aria-label="Select all employees"
          disabled={!isMonthFilterSelected}
        />
      ),
      formatter: (_, row) => (
        <input
          type="checkbox"
          className="pp-checkbox"
          checked={
            isMonthFilterSelected ? selectedIds.includes(row?.user_id) : false
          }
          onChange={
            isMonthFilterSelected ? () => handleSelectRow(row?.user_id) : null
          }
          aria-label={`Select ${row.name}`}
          disabled={!isMonthFilterSelected}
        />
      ),
    },
    {
      dataField: "date",
      text: "Payrole Month",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.date ? getMonthYear(row.date) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "employee",
      text: "Employee",
      formatter: (_, row) => (
        <div className="pp-employee-payroll-employee-info">
          <p className="pp-employee-payroll-employee-name">
            {row?.name || "NA"}
          </p>
        </div>
      ),
    },
    {
      dataField: "account_type",
      text: "Role",
      formatter: (_, row) => (
        <span className="tb-data">{row?.account_type?.replace(/_/g, " ")}</span>
      ),
    },
    {
      dataField: "gym_id",
      text: "Gym Name",
      formatter: (_, row) => (
        <span className="tb-data minWidth150">{row?.gym_name || "NA"}</span>
      ),
    },
    {
      dataField: "net_salary",
      text: "Net Salary",
      formatter: (_, row) => (
        <span className="pp-employee-payroll-net-salary tb-data">
          ₹{convertThenFormat1(row?.net_salary)}
        </span>
      ),
    },
  ];

  return (
    <div className="pp-bulk-table-container">
      <div className="pp-bulk-header">
        <div className="pp-employee-payroll-card-header">
          <span>Employee Payroll Management</span>
          <button variant="dark" type="button" onClick={callFunction}>
            <ReloadIcon />
          </button>
        </div>

        <div className="pp-bulk-actions">
          {isMonthFilterSelected && !exportLoading && !isExportReady ? (
            <button
              className="pp-btn-select-all"
              type="button"
              onClick={handleSelectAll}
              aria-pressed={allSelected}
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          ) : null}

          {exportLoading ? (
            <button className="pp-btn-process" type="button" disabled>
              <span className="pp-btn-process-icon" aria-hidden="true">
                ⏳
              </span>{" "}
              Exporting...
            </button>
          ) : isExportReady && exportData ? (
            <button
              className="pp-btn-download"
              type="button"
              onClick={() => {
                const blob = new Blob([exportData], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `payroll-export-${
                  filters?.month?.value || "data"
                }.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              }}
            >
              <span className="pp-btn-process-icon" aria-hidden="true">
                📥
              </span>{" "}
              Download CSV
            </button>
          ) : (
            <button
              className="pp-btn-process"
              type="button"
              onClick={handleExportSelected}
              disabled={!anySelected}
            >
              <span className="pp-btn-process-icon" aria-hidden="true">
                ✔
              </span>{" "}
              Export Selected ({selectedIds.length})
            </button>
          )}
        </div>
      </div>

      {/* <div className="pp-total-payout-display">
        Total Payout:{" "}
        <span className="pp-salary-green">
          ₹{getTotalPayout().toLocaleString("en-IN")}
        </span>
      </div> */}

      <div className="pp-export-info-text">
        <span className="pp-info-icon">ℹ</span>
        To export data, please select a month from the filter above and choose
        employees from the table below.
      </div>

      <div className="pp-employee-payroll-table-wrapper mt30">
        <PaymentLinkTable
          tableData={allDatas.allData}
          tableColumns={columns}
          tablePagination={allDatas.pagination}
          isPage={isPage}
          setIsPage={(value) => setIsPage(value)}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
        />
      </div>
    </div>
  );
};

export default BulkPayrollProcessing;
